import React from "react";
import styled from "styled-components";

import MuiDialog from "@material-ui/core/Dialog";
import Slide from "@material-ui/core/Slide";

const TOPBAR_HEIGHT = 64;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: ${TOPBAR_HEIGHT}px;
  padding: 0 var(--space-6);
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-color: var(--color-accent);
  }
`;

const Title = styled.h1`
  flex: 1;
  min-width: 0;
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Content = styled.div`
  background: var(--color-bg);
  min-height: calc(100vh - ${TOPBAR_HEIGHT}px);
  padding: var(--space-8) var(--space-6) var(--space-16);
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

type Props = {
  children: React.ReactNode;
  onClose: () => void;
  isOpen: boolean;
  title: string;
  onSave?: () => void;
};

const TransitionComponent = (props: any) => (
  <Slide {...props} direction="up" />
);

const FullScreenDialog = ({ children, onClose, isOpen, title }: Props) => (
  <MuiDialog
    TransitionComponent={TransitionComponent}
    onClose={onClose}
    open={!!isOpen}
    fullScreen
    PaperProps={{
      style: {
        background: "var(--color-bg)",
      },
    }}
  >
    <div className="ds-root">
      <TopBar>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          <CloseIcon />
        </CloseButton>
        <Title>{title}</Title>
      </TopBar>
      <Content>
        <Inner>{children}</Inner>
      </Content>
    </div>
  </MuiDialog>
);

export default FullScreenDialog;
